import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, FileSpreadsheet, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { LosLogo } from "@/components/Logo";

/** Customer-facing clarification form. Asks only for what is actually missing; answers update the project canvas. */
export default function PublicClarificationPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [general, setGeneral] = useState({});
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/clarifications/${token}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const setA = (vid, field, value) => setAnswers((a) => ({ ...a, [vid]: { ...(a[vid] || {}), [field]: value } }));
  const answered = Object.values(answers).reduce((n, f) => n + Object.values(f).filter((v) => v !== "" && v != null).length, 0) + Object.values(general).filter(Boolean).length;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/public/clarifications/${token}`, { answers, general, name });
      setDone(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Shell><p className="text-[14px] text-muted-foreground" data-testid="clarification-error">{error}</p></Shell>;
  if (!data) return <Shell><p className="text-[14px] text-muted-foreground">Loading…</p></Shell>;
  const valveFields = data.fields.filter((f) => f.kind === "valve");
  const generalFields = data.fields.filter((f) => f.kind === "general");

  if (done || data.answered_at) {
    return (
      <Shell>
        <div className="text-center" data-testid="clarification-done">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--v-ok-bg))] text-[hsl(var(--v-ok))]"><Check className="h-6 w-6" strokeWidth={2.5} /></span>
          <h1 className="mt-5 font-display text-[26px] font-bold tracking-[-0.03em]">Thank you</h1>
          <p className="mx-auto mt-2 max-w-[420px] text-[14px] text-muted-foreground">Your answers have been received and the technical configuration for {data.project?.name} is being updated. {data.owner?.name || "Your Skarpenord contact"} will follow up.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell wide>
      <div data-testid="clarification-page">
        <div className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-primary">Clarifications · {data.project?.name}</div>
        <h1 className="mt-2 font-display text-[28px] font-bold leading-tight tracking-[-0.03em] sm:text-[34px]">A few details to finalise the configuration</h1>
        <p className="mt-3 max-w-[640px] text-[14.5px] leading-relaxed text-muted-foreground">{data.intro || `To finalise the technical configuration we need ${data.count} details that were not available in the valve list. Our estimates are shown where we have them – confirm or correct.`}</p>

        {valveFields.length ? (
          <section className="mt-10">
            <h2 className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Valves ({valveFields.length})</h2>
            <ul className="mt-3 divide-y divide-black/[0.06] overflow-hidden rounded-[16px] border border-black/[0.07] bg-white" data-testid="clarification-valves">
              {valveFields.map((f) => (
                <li key={f.valve_id} className="grid gap-4 px-5 py-4 sm:grid-cols-[200px_minmax(0,1fr)]" data-testid={`clarification-valve-${f.tag}`}>
                  <div>
                    <div className="font-mono text-[14px] font-semibold">{f.tag}</div>
                    <div className="mt-0.5 text-[12.5px] text-muted-foreground">{f.context}</div>
                    {f.source ? <div className="mt-1.5 inline-flex items-center gap-1 rounded-[5px] border border-black/[0.07] bg-[hsl(210_20%_96%)] px-1.5 py-px font-mono text-[10.5px] text-muted-foreground"><FileSpreadsheet className="h-2.5 w-2.5" /> {f.source.file} · row {f.source.row}</div> : null}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {f.questions.map((q) => (
                      <label key={q.field} className="block">
                        <span className="text-[12.5px] font-medium">{q.label}</span>
                        {q.type === "select" ? (
                          <select value={answers[f.valve_id]?.[q.field] ?? ""} onChange={(e) => setA(f.valve_id, q.field, e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-black/[0.1] bg-white px-2.5 text-[13.5px] outline-none focus:border-primary/60" data-testid={`clarification-input-${f.tag}-${q.field}`}>
                            <option value="">Select…</option>
                            {q.options.map((o) => <option key={o.value} value={o.value}>{o.label}{q.suggestion === o.value ? " (our assumption)" : ""}</option>)}
                          </select>
                        ) : (
                          <input type="number" inputMode="decimal" value={answers[f.valve_id]?.[q.field] ?? ""} onChange={(e) => setA(f.valve_id, q.field, e.target.value)} placeholder={q.suggestion ? String(Math.round(q.suggestion)) : ""} className="mt-1 h-10 w-full rounded-lg border border-black/[0.1] bg-white px-2.5 font-mono text-[13.5px] outline-none placeholder:text-muted-foreground/50 focus:border-primary/60" data-testid={`clarification-input-${f.tag}-${q.field}`} />
                        )}
                        {q.hint ? <span className="mt-1 flex items-center gap-1 text-[11.5px] text-muted-foreground"><Sparkles className="h-3 w-3 text-[hsl(var(--v-assumed))]" /> {q.hint}</span> : null}
                      </label>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {generalFields.length ? (
          <section className="mt-10">
            <h2 className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Project questions</h2>
            <div className="mt-3 space-y-4" data-testid="clarification-general">
              {generalFields.map((f) => (
                <label key={f.id} className="block rounded-[14px] border border-black/[0.07] bg-white px-5 py-4">
                  <span className="text-[13.5px] font-medium">{f.question}</span>
                  <textarea rows={2} value={general[f.id] || ""} onChange={(e) => setGeneral((g) => ({ ...g, [f.id]: e.target.value }))} className="mt-2 w-full resize-none rounded-lg border border-black/[0.1] bg-white px-3 py-2 text-[13.5px] outline-none focus:border-primary/60" data-testid={`clarification-general-${f.id}`} />
                </label>
              ))}
            </div>
          </section>
        ) : null}

        <div className="mt-10 flex flex-wrap items-end justify-between gap-4 rounded-[16px] border border-black/[0.07] bg-white px-5 py-4">
          <label className="block w-full sm:w-[320px]">
            <span className="text-[12.5px] font-medium">Your name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, company" className="mt-1 h-10 w-full rounded-lg border border-black/[0.1] bg-white px-2.5 text-[13.5px] outline-none focus:border-primary/60" data-testid="clarification-name-input" />
          </label>
          <button type="button" disabled={busy || !answered} onClick={submit} className={cn("inline-flex h-11 items-center gap-2 rounded-full bg-primary px-5 text-[14px] font-semibold text-white disabled:opacity-40")} data-testid="clarification-submit-button">
            {busy ? "Sending…" : `Send ${answered ? `${answered} answer${answered === 1 ? "" : "s"}` : "answers"}`}
          </button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto flex max-w-[960px] items-center justify-between px-6 py-6"><LosLogo className="text-[18px]" /><span className="text-[12px] text-muted-foreground">Rubbestadneset, Norway</span></header>
      <main className={cn("mx-auto px-6 pb-20 pt-4", wide ? "max-w-[960px]" : "max-w-[560px] pt-20")}>{children}</main>
    </div>
  );
}
